import {
    Alert, Col, Container, Row,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import HashLink from '../../components/Utilities/hashLink';
import CustomBreadcrumb, { CustomBreadcrumbItem } from '../../components/Breadcrumb';
import useHtmlTitle from '../../components/Utilities/useHtmlTitle';
import useBodyClass from '../../components/Utilities/useBodyClass';
import BlockUISpinner from '../../components/BlockUISpinner';
import HeaderIntroText from '../../components/HeaderIntroText';

const HelpHowToSetupAccess = () => {
    const [isLoading, setIsLoading] = useState(false);

    const breadcrumbs: CustomBreadcrumbItem[] = [
        { to: '/', text: 'Home' },
        { to: '/help-guide', text: 'Help guide' },
        { to: '', text: 'How to set up access' },
    ];
    useHtmlTitle('How to set up access - Help guide | NMI Services portal');
    useBodyClass('help-guide');

    return (
        <>
            {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )}
            <div aria-busy={isLoading} aria-live='polite' aria-atomic='true'>
                <Container fluid className='default-banner-background mb-5'>
                    <Container>
                        <Row>
                            <Col>
                                <CustomBreadcrumb breadcrumbs={breadcrumbs} />
                            </Col>
                        </Row>
                        <Row className='gs-wrapper'>
                            <Col md={12} lg={9}>
                                <h1 id='page-title' tabIndex={-1} className='banner-title mb-2'>
                                    {'How to set up access to the  '}
                                    <span className='text-nowrap'>NMI Services portal</span>
                                </h1>
                                <HeaderIntroText>
                                    <strong>
                                        Help guide
                                    </strong>
                                </HeaderIntroText>
                            </Col>
                        </Row>
                    </Container>
                </Container>
                <Container>
                    <Row className='mb-4'>
                        <Col md={4} lg={3}>
                            <nav id='navigate-page' className='mb-4' aria-label='On this page'>
                                <h2 className='h4'>On this page</h2>
                                <div className='menu-wrapper'>
                                    <ol>
                                        <li>
                                            <HashLink to='#get-your-digital-id'>
                                                Get your Digital ID
                                            </HashLink>
                                        </li>
                                        <li>
                                            <HashLink to='#portal-access-for-yourself-as-principal-authority'>
                                                Set up your business
                                            </HashLink>
                                        </li>
                                        <li>
                                            <HashLink to='#authorising-portal-access-for-your-staff'>
                                                Authorise others
                                            </HashLink>
                                        </li>
                                    </ol>
                                </div>
                            </nav>
                        </Col>
                        <Col md={8} lg={9}>
                            <h2>
                                {'Step by step guide on setting up access to the '}
                                <span className='text-nowrap'>NMI Services portal</span>
                            </h2>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='get-your-digital-id' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-8' lang='en'>
                                            <h3 className='mb-2'>1. Get your Digital ID</h3>
                                            <p>
                                                {'The myGovID app is a new way to access a range of government online services for personal and business '}
                                                {'matters, instead of usernames and passwords. '}
                                                <br />
                                                <Link to='https://www.mygovid.gov.au/'>
                                                    Learn how to set up your myGovID
                                                </Link>
                                            </p>
                                            <Alert
                                                variant='info'
                                                data-testid='info-summary'
                                                role='status'
                                                aria-live='polite'
                                                className='d-flex mb-4'
                                            >
                                                <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
                                                    <div className='bgCircle mb-3 me-3'>
                                                        <i className='icon-info' aria-hidden='true' />
                                                    </div>
                                                </div>
                                                <div>
                                                    <strong>myGovID is changing to myID</strong>
                                                    <p>
                                                        {'Soon myGovID will be changing its name to myID. It will have a new name and look - but you\'ll continue to use it in the same way.'}
                                                    </p>
                                                    <p>
                                                        {'Learn more about the '}
                                                        <Link to='https://www.mygovid.gov.au/mygovid-changing-myid?utm_source=myGovID%20site%20banner&amp;utm_medium=web&amp;utm_campaign=mygovid_rename_p1'>
                                                            {'new name, what\'s changing and how it affects you'}
                                                        </Link>
                                                        .
                                                    </p>
                                                </div>
                                            </Alert>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='portal-access-for-yourself-as-principal-authority' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-8' lang='en'>
                                            <h3 className='mb-2'>2. Set up your business</h3>

                                            <p>
                                                {'As a '}
                                                <Link to='https://info.authorisationmanager.gov.au/principal-authority'>
                                                    principal authority
                                                </Link>
                                                {' of a company, you must first link your myGovID to the company\'s ABN. You must do this before you can '}
                                                {' authorise access to the NMI Services portal for yourself or anyone else.'}
                                            </p>
                                            <p>If this has not been done for your business before, you can do this yourself as a principal authority:</p>
                                            <ul className='mb-4'>
                                                <li className='mb-2'>
                                                    <strong>Set up your myGovID,</strong>
                                                    {' if you haven\'t already done so.'}
                                                    <br />
                                                    {'You\'ll need a myGovID to log in to ATO\'s Relationship Authorisation Manager later.'}
                                                    <br />
                                                    <Link to='https://www.mygovid.gov.au/'>
                                                        Learn how to set up your myGovID
                                                    </Link>
                                                    .
                                                </li>
                                                <li className='mb-2'>
                                                    <strong>Contact the ATO</strong>
                                                    {' to link your myGovID to your company\'s ABN. '}
                                                    <br />
                                                    <Link to='https://info.authorisationmanager.gov.au/principal-authority#Howtolinkyourbusiness'>
                                                        Find out how to link your business
                                                    </Link>
                                                    .
                                                </li>
                                                <li className='mb-2'>
                                                    <strong>Log in to RAM</strong>
                                                    {' and authorise yourself to act on behalf of your business in the NMI Services portal. '}
                                                    <br />
                                                    {'You can also nominate someone to act as an '}
                                                    <Link to='https://info.authorisationmanager.gov.au/authorised-users-and-administrators'>
                                                        authorisation administrator
                                                    </Link>
                                                    .
                                                </li>
                                                <li className='mb-2'>
                                                    <strong>Log in to NMI Services portal</strong>
                                                    {' and set up your business details in the NMI Services portal.'}
                                                    <br />
                                                    <Link to='/'>
                                                        Log in to NMI Services portal
                                                    </Link>
                                                    .
                                                </li>
                                            </ul>
                                            <p>
                                                {'You can find additional help and support on the '}
                                                <Link to='https://info.authorisationmanager.gov.au/help#where_can_i_go_for_additional_help_or_support'>
                                                    Relationship Authorisation Manager help
                                                </Link>
                                                {' page including contact details.'}
                                            </p>

                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section ' id='authorising-portal-access-for-your-staff' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-8' lang='en'>
                                            <h3 className='mb-2'>3. Authorise others</h3>
                                            <p>
                                                {'The '}
                                                <Link to='https://info.authorisationmanager.gov.au/principal-authority'>
                                                    principal authority or authorisation administrator
                                                </Link>
                                                {' for your company will need to authorise staff to access the NMI Services portal. This will make them an '}
                                                <Link to='https://info.authorisationmanager.gov.au/authorised-users-and-administrators'>
                                                    authorised user
                                                </Link>
                                                .
                                            </p>
                                            <ol>
                                                <li>
                                                    <strong>Request</strong>
                                                    {' the staff member to '}
                                                    <Link to='https://www.mygovid.gov.au/'>
                                                        set up their myGovID
                                                    </Link>
                                                    .
                                                </li>
                                                <li>
                                                    <strong>Ask</strong>
                                                    {' the staff member for their full name as used for their myGovID, as well as their preferred email address for access code.'}
                                                </li>
                                                <li>
                                                    <strong>Login</strong>
                                                    {' to the ATO\'s '}
                                                    <Link to='https://authorisationmanager.gov.au/'>
                                                        Relationship Authorisation manager (RAM)
                                                    </Link>
                                                    .
                                                </li>
                                                <li>
                                                    <strong>Authorise </strong>
                                                    {'the staff member\'s personal myGovID to work on behalf of the company using RAM. See '}
                                                    <Link to='https://info.authorisationmanager.gov.au/set-up-authorisations'>
                                                        set up authorisations
                                                    </Link>
                                                    {' and '}
                                                    <Link to='https://info.authorisationmanager.gov.au/manage-authorisations'>
                                                        manage authorisations
                                                    </Link>
                                                    {' for more details.'}
                                                </li>
                                                <li>
                                                    <strong>Inform</strong>
                                                    {' the staff member that they have been authorised and will receive an authorisation code which they need '}
                                                    {'to activate in RAM within 7 days. Once activated they\'ll be able to login to the NMI Services portal.'}
                                                </li>
                                            </ol>
                                            <p>
                                                {'You can find additional help and support on the '}
                                                <Link to='https://info.authorisationmanager.gov.au/help#where_can_i_go_for_additional_help_or_support'>
                                                    Relationship Authorisation Manager help
                                                </Link>
                                                {' page including contact details.'}
                                            </p>
                                        </div>

                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4} lg={3} />
                        <Col md={8} lg={9}>
                            <div className='mt-5 d-grid w-100 gap-3 d-md-flex justify-content-md-between'>
                                <Link
                                    data-testid='back-to-help-guide'
                                    to='/help-guide'
                                    replace
                                    className='btn btn-tertiary'
                                >
                                    <i className='icon-back me-1' aria-hidden='true' />
                                    Back to help guide
                                </Link>
                                <Link
                                    data-testid='next-faqs'
                                    to='/help-guide/faqs/'
                                    replace
                                    className='btn btn-secondary'
                                >
                                    {'View '}
                                    <abbr title='Frequently Asked Questions'>FAQs</abbr>
                                </Link>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default HelpHowToSetupAccess;
